import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { CamposLocalidade } from '../components/CamposLocalidade';
import { rotuloDoIdioma } from '../dados/idiomas';
import {
  Campo,
  Carregando,
  ErroBox,
  Form,
  LinhaCampos,
  Modal,
  ModalRodape,
  PageHeader,
} from '../components/ui';
import {
  CORES_STATUS,
  ROTULOS_STATUS,
  cidadeDoEvento,
  formatarPeriodo,
  limpar,
  rotuloFormato,
} from './Eventos';
import { ConteudoDoEvento } from './EventoConteudo';

interface EventoDetalhado {
  id: string;
  name: string;
  shortDescription: string | null;
  fullDescription: string | null;
  type: string | null;
  format: string;
  venue: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  startDate: string | null;
  endDate: string | null;
  startTime: string | null;
  endTime: string | null;
  timezone: string;
  website: string | null;
  status: string;
  primaryLocale: string;
  transicoesPermitidas: string[];
  _count: { participants: number; objectives: number; themes: number; activities: number };
}

export function EventoDetalhe() {
  const { id = '' } = useParams();
  const { pode, liberado } = useAuth();
  const cliente = useQueryClient();
  const [mudandoStatus, setMudandoStatus] = useState<string | null>(null);
  const [editando, setEditando] = useState(false);

  const consulta = useQuery({
    queryKey: ['evento', id],
    queryFn: () => api<EventoDetalhado>(`/events/${id}`),
  });

  const alterarStatus = useMutation({
    mutationFn: (status: string) => api(`/events/${id}`, { method: 'PATCH', body: { status } }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['evento', id] });
      cliente.invalidateQueries({ queryKey: ['eventos'] });
      setMudandoStatus(null);
    },
  });

  const salvar = useMutation({
    mutationFn: (dados: Record<string, string>) =>
      api(`/events/${id}`, { method: 'PATCH', body: limpar(dados) }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['evento', id] });
      cliente.invalidateQueries({ queryKey: ['eventos'] });
      setEditando(false);
    },
  });

  if (consulta.isPending) return <Carregando />;
  if (consulta.isError) return <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />;

  const evento = consulta.data;

  return (
    <>
      <PageHeader
        titulo={evento.name}
        subtitulo={
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <Tag type={CORES_STATUS[evento.status] ?? 'muted'}>
              {ROTULOS_STATUS[evento.status] ?? evento.status}
            </Tag>
            {evento.shortDescription}
          </span>
        }
        acoes={
          <>
            {pode('events.update') && (
              <button className="btn btn-secondary" onClick={() => setEditando(true)}>
                <i className="ti ti-edit" /> Editar
              </button>
            )}
            {/* Não há botão "Conteúdo" nem "Participantes": participantes,
                programação, palestrantes, objetivos e temas
                estão logo abaixo, em abas, nesta mesma tela. Botão que levava a
                outro endereço fazia parecer que eram coisas diferentes — e são
                todas o mesmo evento. */}
            {/* A jornada é o único caminho que manda mensagem para gente de
                verdade. Enquanto a instalação não liberar (`JOURNEYS_ENABLED`),
                o botão fica visível e desabilitado — some seria pior, porque
                quem monta o evento precisa saber que essa etapa existe. */}
            {liberado('jornadas') ? (
              <Link className="btn btn-secondary" to={`/eventos/${id}/jornadas`}>
                <i className="ti ti-route" /> Jornadas
              </Link>
            ) : (
              <button
                className="btn btn-secondary"
                disabled
                title="A jornada de WhatsApp ainda não foi liberada nesta instalação."
              >
                <i className="ti ti-route" /> Jornadas
                <span className="marca-em-breve">Em breve</span>
              </button>
            )}
            {/* As indicações vêm DEPOIS da conversa: o cruzamento lê o perfil
                que a jornada constrói. O botão fica ao lado por isso, e na
                mesma ordem em que as duas coisas acontecem. */}
            {pode('recommendations.read') &&
              (liberado('jornadas') ? (
                <Link className="btn btn-secondary" to={`/eventos/${id}/recomendacoes`}>
                  <i className="ti ti-affiliate" /> Recomendações
                </Link>
              ) : (
                <button
                  className="btn btn-secondary"
                  disabled
                  title="O cruzamento depende da jornada, que ainda não foi liberada nesta instalação."
                >
                  <i className="ti ti-affiliate" /> Recomendações
                  <span className="marca-em-breve">Em breve</span>
                </button>
              ))}
            {pode('events.update') && evento.transicoesPermitidas.length > 0 && (
              <select
                className="form-select"
                style={{ width: 'auto' }}
                value=""
                onChange={(e) => e.target.value && setMudandoStatus(e.target.value)}
              >
                <option value="">Mudar status…</option>
                {evento.transicoesPermitidas.map((s) => (
                  <option key={s} value={s}>
                    {ROTULOS_STATUS[s] ?? s}
                  </option>
                ))}
              </select>
            )}
          </>
        }
      />

      {/* Contadores, não navegação.

          Levavam a outras telas, e faziam sentido enquanto essas telas
          existiam. Agora o que eles contam está em abas logo abaixo: um clique
          trocaria uma aba fora do campo de visão, e o cartão pareceria não ter
          feito nada. Cartão que parece botão e não se comporta como botão é
          pior do que cartão que não parece botão. */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <Estatistica
          icone="ti-users"
          numero={evento._count.participants}
          rotulo="Participantes"
        />
        <Estatistica
          icone="ti-calendar-time"
          numero={evento._count.activities}
          rotulo="Atividades"
        />
        <Estatistica icone="ti-target" numero={evento._count.objectives} rotulo="Objetivos" />
        <Estatistica icone="ti-tags" numero={evento._count.themes} rotulo="Temas" />
      </div>

      {/* Sem `grade-detalhe`: ela é `2fr 1fr`, e com um cartão só sobrava um
          terço de espaço vazio à direita. */}
      <div>
        <div className="card">
          <div className="secao-titulo">
            <i className="ti ti-info-circle" /> Dados do evento
          </div>

          <dl className="lista-dados">
            <dt>Período</dt>
            <dd>
              {formatarPeriodo(evento.startDate, evento.endDate)}
              {evento.startTime && ` · ${evento.startTime}${evento.endTime ? `–${evento.endTime}` : ''}`}
            </dd>

            <dt>Formato</dt>
            <dd>{rotuloFormato(evento.format)}</dd>

            {evento.type && (
              <>
                <dt>Tipo</dt>
                <dd>{evento.type}</dd>
              </>
            )}

            <dt>Local</dt>
            <dd>
              {evento.venue || cidadeDoEvento(evento) || '—'}
              {evento.venue && cidadeDoEvento(evento) && (
                <div className="celula-secundaria">{cidadeDoEvento(evento)}</div>
              )}
              {evento.address && <div className="celula-secundaria">{evento.address}</div>}
            </dd>

            <dt>Fuso</dt>
            <dd>{rotuloDoFuso(evento.timezone)}</dd>

            <dt>Idioma</dt>
            <dd>{rotuloDoIdioma(evento.primaryLocale)}</dd>

            {evento.website && (
              <>
                <dt>Site</dt>
                <dd>
                  <a href={evento.website} target="_blank" rel="noreferrer">
                    {evento.website}
                  </a>
                </dd>
              </>
            )}
          </dl>

          {evento.fullDescription && (
            <>
              <div className="divider" style={{ margin: '18px 0' }} />
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' }}>
                {evento.fullDescription}
              </div>
            </>
          )}
        </div>

        {/* Aqui existia um cartão "Parâmetros da jornada", com ritmo de envio,
            score mínimo e limite de lembretes.

            Saiu por dois motivos. O primeiro é de lugar: quem monta o evento
            está cuidando de programação, palestrantes e patrocinadores — ritmo
            de disparo é assunto de quem opera a jornada, e mostrá-lo aqui pedia
            uma decisão fora de hora.

            O segundo é que dois dos três números não eram lidos por ninguém.
            `messagesPerMinute` não tem consumidor: quem dita o ritmo é o worker,
            por `QUEUE_POLL_INTERVAL_MS` e `QUEUE_BATCH_SIZE`, que são globais.
            `minScore` é do matchmaking, que é fase 4. Só `maxReminders` valia — e
            o aviso do cartão prometia que os três passariam a valer quando a
            jornada fosse liberada, o que era falso para dois.

            As configurações que de fato mandam no comportamento — a janela de
            envio e o intervalo entre lembretes — nunca estiveram nesta tela.
            Quando ganharem lugar, é na tela de jornadas do evento. */}
      </div>

      {/* O conteúdo do evento, na mesma tela. Os cartões de estatística acima
          apontam para as abas daqui pelo `?aba=`, então clicar em "Temas" rola
          para a aba certa em vez de trocar de endereço. */}
      <ConteudoDoEvento />

      {editando && (
        <Modal
          titulo="Editar evento"
          descricao="Campo deixado em branco fica como está."
          aoFechar={() => setEditando(false)}
          largura={620}
        >
          <Form aoEnviar={(dados) => salvar.mutate(dados)}>
            {salvar.isError && <div className="form-erro">{(salvar.error as Error).message}</div>}

            <Campo label="Nome do evento" name="name" obrigatorio valorInicial={evento.name} />
            <Campo
              label="Descrição curta"
              name="shortDescription"
              valorInicial={evento.shortDescription ?? ''}
              ajuda="Aparece nas listagens e no contexto que a IA usa na conversa."
            />

            <LinhaCampos>
              <Campo label="Tipo" name="type" valorInicial={evento.type ?? ''} />
              <Campo
                label="Formato"
                name="format"
                valorInicial={evento.format}
                opcoes={[
                  { valor: 'PRESENCIAL', rotulo: 'Presencial' },
                  { valor: 'ONLINE', rotulo: 'Online' },
                  { valor: 'HIBRIDO', rotulo: 'Híbrido' },
                ]}
              />
            </LinhaCampos>

            <LinhaCampos>
              {/* A API devolve data com hora e fuso; o `input[type=date]` só
                  aceita os dez primeiros caracteres. */}
              <Campo
                label="Início"
                name="startDate"
                tipo="date"
                valorInicial={evento.startDate?.slice(0, 10) ?? ''}
              />
              <Campo
                label="Término"
                name="endDate"
                tipo="date"
                valorInicial={evento.endDate?.slice(0, 10) ?? ''}
              />
            </LinhaCampos>

            <LinhaCampos>
              <Campo
                label="Hora de início"
                name="startTime"
                tipo="time"
                valorInicial={evento.startTime ?? ''}
              />
              <Campo
                label="Hora de término"
                name="endTime"
                tipo="time"
                valorInicial={evento.endTime ?? ''}
              />
            </LinhaCampos>

            <CamposLocalidade
              paisInicial={evento.country ?? 'BR'}
              estadoInicial={evento.state ?? ''}
              cidadeInicial={evento.city ?? ''}
            />

            <LinhaCampos>
              <Campo
                label="Local"
                name="venue"
                valorInicial={evento.venue ?? ''}
                placeholder="Centro de convenções, hotel…"
              />
              <Campo label="Site" name="website" valorInicial={evento.website ?? ''} />
            </LinhaCampos>

            <Campo label="Endereço" name="address" valorInicial={evento.address ?? ''} />

            <Campo
              label="Descrição completa"
              name="fullDescription"
              linhas={4}
              valorInicial={evento.fullDescription ?? ''}
              ajuda="Quanto mais contexto, melhor a conversa da IA com o participante."
            />

            <ModalRodape>
              <button type="button" className="btn btn-secondary" onClick={() => setEditando(false)}>
                Cancelar
              </button>
              <button className="btn btn-primary" type="submit" disabled={salvar.isPending}>
                {salvar.isPending ? 'Salvando…' : 'Salvar alterações'}
              </button>
            </ModalRodape>
          </Form>
        </Modal>
      )}

      {mudandoStatus && (
        <Modal
          titulo="Mudar status do evento"
          aoFechar={() => setMudandoStatus(null)}
          largura={440}
        >
          {alterarStatus.isError && (
            <div className="form-erro">{(alterarStatus.error as Error).message}</div>
          )}

          <p style={{ fontSize: 13, lineHeight: 1.55, color: 'var(--text-muted)' }}>
            De <strong style={{ color: 'var(--text)' }}>{ROTULOS_STATUS[evento.status]}</strong> para{' '}
            <strong style={{ color: 'var(--text)' }}>{ROTULOS_STATUS[mudandoStatus]}</strong>.
          </p>

          {mudandoStatus === 'ATIVO' && (
            <div className="aviso" style={{ marginTop: 12 }}>
              <i className="ti ti-alert-triangle" />
              <span>
                Ativar o evento é o que libera o disparo de jornadas para os participantes.
              </span>
            </div>
          )}

          <ModalRodape>
            <button className="btn btn-secondary" onClick={() => setMudandoStatus(null)}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              onClick={() => alterarStatus.mutate(mudandoStatus)}
              disabled={alterarStatus.isPending}
            >
              {alterarStatus.isPending ? 'Alterando…' : 'Confirmar'}
            </button>
          </ModalRodape>
        </Modal>
      )}
    </>
  );
}

function Estatistica({
  icone,
  numero,
  rotulo,
  para,
}: {
  icone: string;
  numero: number;
  rotulo: string;
  para?: string;
}) {
  const conteudo = (
    <div className="stat-card">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <i className={`ti ${icone}`} style={{ color: 'var(--text-dim)', fontSize: 15 }} />
        <span className="resumo-rotulo" style={{ margin: 0 }}>
          {rotulo}
        </span>
      </div>
      <div className="resumo-numero" style={{ marginTop: 6 }}>
        {numero}
      </div>
    </div>
  );

  return para ? (
    <Link to={para} style={{ textDecoration: 'none' }}>
      {conteudo}
    </Link>
  ) : (
    conteudo
  );
}

/**
 * "Horário de Brasília (GMT-3)" no lugar de "America/Sao_Paulo".
 *
 * O nome e o deslocamento vêm do próprio navegador, em português. O deslocamento
 * é o de hoje — num fuso com horário de verão ele muda ao longo do ano, e é isso
 * mesmo que quem lê precisa saber. Fuso que o navegador não conhece sai cru.
 */
function rotuloDoFuso(fuso: string) {
  try {
    const parte = (estilo: 'longGeneric' | 'shortOffset') =>
      new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, timeZoneName: estilo })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value;

    const nome = parte('longGeneric');
    const deslocamento = parte('shortOffset');
    return nome && deslocamento ? `${nome} (${deslocamento})` : fuso;
  } catch {
    return fuso;
  }
}
